import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ClipboardList, CalendarCheck2, ChevronRight, Wrench, Mic } from "lucide-react";
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Separator } from "@/components/ui/separator";
import { getRecentMachines } from "@/lib/recent";
import { cn } from "@/lib/utils";

const ActionCard = ({ icon: Icon, title, desc, onClick, testid, tone = "dark", compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={cn(
      "pressable flex w-full items-center gap-3 rounded-[18px] border text-left",
      compact ? "min-h-[64px] p-3" : "min-h-[72px] gap-4 p-4",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
      tone === "dark" ? "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]" : "bg-card hover:bg-accent"
    )}
  >
    <span className={cn("grid shrink-0 place-items-center rounded-[14px]", compact ? "h-10 w-10" : "h-12 w-12", tone === "dark" ? "bg-primary text-[#0B0D10]" : "bg-muted text-foreground")}>
      <Icon className={compact ? "h-5 w-5" : "h-6 w-6"} />
    </span>
    <span className="min-w-0 flex-1">
      <span className={cn("block font-heading font-bold leading-tight", compact ? "text-sm" : "text-base")}>{title}</span>
      <span className={cn("mt-0.5 block truncate text-xs", tone === "dark" ? "opacity-70" : "text-muted-foreground")}>{desc}</span>
    </span>
    {!compact && <ChevronRight className="h-5 w-5 shrink-0 opacity-60" />}
  </button>
);

/** Mobile «Ny»-drawer: report type choice + shortcuts + recent machines. */
export const NewReportDrawer = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [machineId, setMachineId] = useState("");

  useEffect(() => {
    if (open) {
      setRecent(getRecentMachines());
      setMachineId("");
    }
  }, [open]);

  const go = (path, extra = {}) => {
    onOpenChange(false);
    const q = new URLSearchParams(extra);
    if (machineId) q.set("machine", machineId);
    const qs = q.toString();
    navigate(qs ? `${path}?${qs}` : path);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="new-report-drawer">
        <DrawerHeader>
          <DrawerTitle>Ny rapport</DrawerTitle>
          <DrawerDescription>{machineId ? "Maskin vald – vel rapporttype." : "Kva vil du registrere?"}</DrawerDescription>
        </DrawerHeader>
        <DrawerBody className="space-y-3 pb-[calc(var(--safe-bottom)+20px)]">
          <ActionCard icon={Mic} title="Snakk inn rapport" desc="Ta opp – AI fyller ut felta for deg" onClick={() => go("/rapportar/ny-service", { voice: "1" })} testid="new-report-voice" />
          <div className="grid grid-cols-2 gap-2">
            <ActionCard icon={FileText} title="Servicerapport" desc="Oljeskift, filter" onClick={() => go("/rapportar/ny-service")} testid="new-report-service" tone="light" compact />
            <ActionCard icon={ClipboardList} title="Jobbrapport" desc="Reparasjon, anna" onClick={() => go("/rapportar/ny-jobb")} testid="new-report-jobb" tone="light" compact />
          </div>

          {recent.length > 0 && (
            <div className="pt-1" data-testid="new-report-recent">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Siste maskiner</span>
                {machineId && (
                  <button type="button" className="text-xs font-semibold text-primary" onClick={() => setMachineId("")} data-testid="new-report-recent-clear">Nullstill</button>
                )}
              </div>
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {recent.map((m) => {
                  const active = machineId === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMachineId(active ? "" : m.id)}
                      aria-pressed={active}
                      data-testid={`new-report-recent-${m.id}`}
                      className={cn(
                        "pressable flex h-12 shrink-0 items-center gap-2 rounded-full border px-3.5 text-left text-sm",
                        active ? "border-transparent bg-primary text-[#0B0D10]" : "bg-card"
                      )}
                    >
                      <Wrench className="h-4 w-4 shrink-0 opacity-70" />
                      <span className="min-w-0">
                        <span className="block max-w-[160px] truncate font-semibold leading-tight">{m.label}</span>
                        {m.customer_name && <span className={cn("block max-w-[160px] truncate text-[11px] leading-tight", active ? "opacity-80" : "text-muted-foreground")}>{m.customer_name}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Separator className="my-1" />
          <button
            type="button"
            onClick={() => { onOpenChange(false); navigate("/kalender?view=list"); }}
            data-testid="new-report-plan-deadline"
            className="pressable flex min-h-[56px] w-full items-center gap-3 rounded-[14px] px-2 text-left hover:bg-accent"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted"><CalendarCheck2 className="h-5 w-5" /></span>
            <span className="flex-1">
              <span className="block text-sm font-semibold">Planlegg frist</span>
              <span className="block text-xs text-muted-foreground">Gå til fristlista i kalenderen</span>
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
